import { useCallback, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { AdminContext, useAdmin } from "./admin";
import type { AdminContextValue, Org, Site } from "./admin";
import { useSignedUrl } from "./lib/files";
import { usePath } from "./router";
import { errorText, supabase } from "./supabase";
import Dashboard from "./pages/Dashboard";
import Editor from "./pages/Editor";
import Export from "./pages/Export";
import Library from "./pages/Library";
import People from "./pages/People";
import Settings from "./pages/Settings";
import SignIn from "./pages/SignIn";

type Loaded =
  | { state: "loading" }
  | { state: "signed-out" }
  | { state: "not-admin"; email: string }
  | { state: "error"; message: string }
  | { state: "ready"; value: Omit<AdminContextValue, "reload"> };

async function loadAdmin(session: Session): Promise<Loaded> {
  const { data: admin, error } = await supabase
    .from("org_admin")
    .select("name, can_approve, org_id")
    .eq("auth_user_id", session.user.id)
    .maybeSingle();
  if (error) return { state: "error", message: errorText(error) };
  if (!admin) return { state: "not-admin", email: session.user.email ?? "" };
  const [org, sites] = await Promise.all([
    supabase.from("org").select("id, name, logo_path, scenarios_per_week").eq("id", admin.org_id).single(),
    supabase.from("site").select("id, name, client_type").order("name"),
  ]);
  if (org.error || sites.error) return { state: "error", message: errorText(org.error ?? sites.error) };
  return {
    state: "ready",
    value: {
      session,
      admin: { name: admin.name, can_approve: admin.can_approve },
      org: org.data as Org,
      sites: sites.data as Site[],
    },
  };
}

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, []);

  const userId = session?.user.id;
  const reload = useCallback(async () => {
    if (session) setLoaded(await loadAdmin(session));
  }, [session]);

  useEffect(() => {
    if (session === undefined) return;
    if (session === null) {
      setLoaded({ state: "signed-out" });
      return;
    }
    setLoaded((l) => (l.state === "ready" ? l : { state: "loading" }));
    loadAdmin(session).then(setLoaded);
    // Only a different person needs a fresh load; token refreshes keep the page.
  }, [userId, session === null]);

  if (loaded.state === "loading") return <div className="empty">Loading…</div>;
  if (loaded.state === "signed-out") return <SignIn />;
  if (loaded.state === "not-admin" || loaded.state === "error") {
    return (
      <div className="signin">
        <div className="card">
          <h1>{loaded.state === "error" ? "Something went wrong" : "Not a training lead"}</h1>
          <p className="muted">
            {loaded.state === "error"
              ? loaded.message
              : `${loaded.email} is not set up as a training lead for any company. Ask Chaukas support to add you.`}
          </p>
          <button className="btn quiet" onClick={() => supabase.auth.signOut()}>
            Sign out
          </button>
        </div>
      </div>
    );
  }
  return (
    <AdminContext.Provider value={{ ...loaded.value, reload }}>
      <Shell />
    </AdminContext.Provider>
  );
}

const PAGES = [
  { path: "/", name: "Dashboard" },
  { path: "/scenarios", name: "Scenarios" },
  { path: "/people", name: "People" },
  { path: "/export", name: "Export" },
  { path: "/settings", name: "Settings" },
];

function Shell() {
  const path = usePath();
  const { admin, org } = useAdmin();
  return (
    <>
      <TopBar path={path} name={admin.name} org={org} />
      <main>
        <Page path={path} />
      </main>
    </>
  );
}

function Page({ path }: { path: string }) {
  const editing = path.match(/^\/scenarios\/(new|[0-9a-f-]{36})$/);
  if (editing) return <Editor key={editing[1]} id={editing[1] === "new" ? null : editing[1]} />;
  if (path.startsWith("/scenarios")) return <Library />;
  if (path === "/people") return <People />;
  if (path === "/export") return <Export />;
  if (path === "/settings") return <Settings />;
  return <Dashboard />;
}

function TopBar({ path, name, org }: { path: string; name: string; org: Org }) {
  const logo = useSignedUrl("client", org.logo_path);
  const section = "/" + (path.split("/")[1] ?? "");
  return (
    <header className="topbar">
      <div className="brand">
        {logo ? <img className="client" src={logo} alt={org.name} /> : <span>{org.name}</span>}
        <span className="divider" />
        <span className="chaukas">
          <img src="/icon.svg" alt="" />
          Chaukas
        </span>
      </div>
      <nav className="nav">
        {PAGES.map((p) => (
          <a key={p.path} href={"#" + p.path} className={section === p.path ? "active" : ""}>
            {p.name}
          </a>
        ))}
      </nav>
      <div className="who">
        <span>{name}</span>
        <button className="btn quiet small" onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </div>
    </header>
  );
}
